<template>
  <div class="title-bar-container">
    <div v-if="disabled" class="disabled-box"></div>
    <div v-if="!isTitleBarHidden()" class="flex-container">
      <div style="position: relative">
        <Avatar @click="state.isAvatarMenuOpen = !state.isAvatarMenuOpen" :firstName="user.firstName" :lastName="user.lastName" />
        <vwc-menu menucorner="START" corner="TOP_LEFT" quick absolute x="0" y="0" :open="state.isAvatarMenuOpen" @closed="state.isAvatarMenuOpen = false">
          <vwc-list-item role="menuitem" class="first-row" tabindex="0" mwc-list-item="">
            <div class="avatar-row">
              <Avatar size="lg" :firstName="user.firstName" :lastName="user.lastName" class="no-shrink" />
              <div class="user-details-container user-details-container-in-menu">
                <div>
                  <strong class="user-name">{{ user.firstName }} {{ user.lastName }}</strong>
                </div>
                <strong class="hidden-desktop"
                  >DID(s): {{ activeExtension.didList && activeExtension.didList.length > 0 ? activeExtension.didList.join(', ') : 'N/A' }}</strong
                >
              </div>
            </div>
          </vwc-list-item>
          <div class="separator" />
          <vwc-list-item @click="onLogoutClick" role="menuitem" class="middle-row">
            <div class="menu-row flex-start">
              <vwc-icon type="quit" size="small" class="menu-icon"></vwc-icon>
              Logout
            </div>
          </vwc-list-item>
          <div class="separator" />

          <vwc-list-item role="menuitem" class="last-row middle-row">
            <div class="terms-and-privacy">
              <div>
                <a href="https://www.vonage.com/legal/" target="_blank">Terms of Service & Privacy Policy</a>
              </div>
              <span class="grey">Version: {{ appVersion }} - {{ gitRevision }}</span>
            </div>
          </vwc-list-item>
        </vwc-menu>
      </div>
      <div class="user-details-container hidden-mobile">
        <div>
          <strong class="user-name">{{ user.firstName }} {{ user.lastName }}</strong>
        </div>

        <strong>Direct Dial Number(s): {{ activeExtension.didList && activeExtension.didList.length > 0 ? activeExtension.didList.join(', ') : 'N/A' }}</strong>
      </div>
    </div>
    <div v-if="!isActionButtonHidden()">
      <vwc-button
        v-for="(button, i) in buttons"
        class="title-bar-button"
        @click="onClick(button)"
        :key="i"
        :icon="(button.icon || '').replace('Vlt-icon-', '')"
        :layout="button.outline ? 'outlined' : 'filled'"
        :shape="'pill'"
      >
        {{ button.text }}
      </vwc-button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive } from 'vue';
import { useHost } from '@/modules/host';
import get from 'lodash.get';
import Avatar from '@/components/Avatar.vue';
import { useExtensions, useUser } from '@/modules/user';
import { sendEventToHostedApp } from '@/composable/apps-renderer';

const TITLE_BAR_BUTTON_CLICKED_EVENT = 'title-bar-button-clicked';
const LOGOUT_EVENT = 'logout';

export default defineComponent({
  name: 'TitleBar',
  props: {
    appId: {
      type: String,
      default: '',
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    data: {
      type: Object,
      default: () => ({}),
    },
  },

  components: {
    Avatar,
  },

  methods: {
    isMobile() {
      const appleRegex = /iPhone|iPad|iPod/i;
      const androidRegex = /Android|webOS|Mobile|Opera Mini|IEMobile|Blackberry/i;
      return navigator.userAgent.match(appleRegex) || navigator.userAgent.match(androidRegex);
    },
    isTitleBarHidden() {
      return (this.isMobile() && this.appId === 'fax') || this.appId === 'MSTeamsCalls';
    },
    isActionButtonHidden() {
      return (this.isMobile() && this.appId === 'fax') || this.appId === 'MSTeamsCalls';
    },
  },
  setup(props) {
    const state = reactive({
      isAvatarMenuOpen: false,
    });

    const { logout } = useHost();
    const { user } = useUser();
    const { activeExtension } = useExtensions();

    const buttons = get(props.data, 'buttons', []);
    const appVersion = process.env.VUE_APP_VERSION;
    const gitRevision = process.env.VUE_APP_GIT_REVISION;

    const onLogoutClick = async () => {
      await logout();
      try {
        await sendEventToHostedApp(props.appId, LOGOUT_EVENT);
      } catch (e) {
        console.error(`Error sending event - ${e}`);
      }
    };
    const onClick = async (button) => {
      try {
        await sendEventToHostedApp(props.appId, TITLE_BAR_BUTTON_CLICKED_EVENT, { event: button.event });
      } catch (e) {
        console.error(`Error sending event - ${e}`);
      }
    };
    return {
      state,
      onLogoutClick,
      onClick,
      buttons,
      user,
      activeExtension,
      appVersion,
      gitRevision,
    };
  },
});
</script>

<style scoped>
.title-bar-container {
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}

.disabled-box {
  width: 100%;
  height: 100%;
  z-index: 99;
  background-color: #0d0d0d;
  opacity: 0.5;
  position: absolute;
  margin-left: -16px;
}

.flex-container {
  display: flex;
}

.user-details-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-left: 14px;
}

.title-bar-button {
  margin-right: 8px;
}

.avatar-row {
  display: flex;
  flex-direction: row;
}

.menu-row {
  display: flex;
  flex-direction: row;
  font-size: 14px;
  min-height: 45px;
  align-items: center;
  justify-content: space-between;
  height: 45px;
}

.user-details-container-in-menu {
  justify-content: center;
}

.user-name {
  font-size: 16px;
}

.separator {
  background-color: #e1e2e6;
  height: 1px;
}

.menu-icon {
  margin-right: 8px;
  align-self: center;
}

.grey {
  color: #9b9da3;
  align-self: flex-start;
}

.flex-start {
  justify-content: flex-start;
}

.no-underline {
  font-size: 12px;
  text-decoration: none;
}

.terms-and-privacy {
  display: flex;
  flex-direction: column;
  font-size: 12px;
}

.last-row {
  margin: 8px 0;
}

.first-row {
  padding: 0 12px;
  height: 88px;
  width: 230px;
}

.middle-row {
  padding: 0 16px;
}

.no-shrink {
  flex-shrink: 0;
}

@media only screen and (max-width: 575px) {
  .hidden-mobile {
    display: none;
  }
}

@media only screen and (min-width: 576px) {
  .hidden-desktop {
    display: none;
  }
}
</style>
